import { useState } from "react";
import { ChevronDown, Loader2, Check } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { ThinkingTraceEntry } from "../types/playground";

/** Default logo used for core/platform tools without a specific integration. */
const PRODGY_LOGO_URL = "/assets/logo.png";

interface ThinkingTraceProps {
  /** Structured trace persisted on the assistant turn — paragraphs + tool calls. */
  entries: ThinkingTraceEntry[];
  /** Trace is currently being built by the stream. Keeps the section expanded
   * and shows the live caret on the last text entry. */
  isStreaming?: boolean;
  /** Label shown in the header. */
  title?: string;
  /** Resolve the integration logo URL for a given MCP tool name. Returns null
   * for unknown / core tools — the trace falls back to the Prodgy logo. */
  getToolLogo?: (toolName: string) => string | null;
  /** O rastro desta resposta não pôde ser carregado: o bloco aparece só com este aviso. */
  unavailableNote?: string;
}

type TextEntry = Extract<ThinkingTraceEntry, { kind: 'text' }>;
type ToolEntry = Extract<ThinkingTraceEntry, { kind: 'tool' }>;

interface TraceStep {
  text?: TextEntry;
  tools: ToolEntry[];
}

/** Group consecutive entries so each text paragraph "owns" the tools after it. */
function groupTraceEntries(entries: ThinkingTraceEntry[]): TraceStep[] {
  const groups: TraceStep[] = [];
  let current: TraceStep | null = null;
  for (const entry of entries) {
    if (entry.kind === 'text') {
      if (current) groups.push(current);
      current = { text: entry, tools: [] };
    } else {
      if (!current) current = { tools: [] };
      current.tools.push(entry);
    }
  }
  if (current) groups.push(current);
  return groups;
}

function trunc(s: string, max = 40): string {
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function getArgHint(args: Record<string, unknown> | undefined): string {
  if (!args) return "";
  const priorityKeys = [
    "query", "q", "searchQuery", "search_query", "wiql", "searchText", "search_text",
    "path", "filePath", "file_path", "repositoryPath", "repository_path", "scopePath",
    "title", "name", "summary", "subject",
    "id", "workItemId", "work_item_id", "itemId", "taskId",
    "text",
  ];
  for (const key of priorityKeys) {
    const val = args[key];
    if (val && typeof val === "string") return trunc(val, 40);
  }
  for (const val of Object.values(args)) {
    if (val && typeof val === "string") return trunc(val, 40);
  }
  return "";
}

function formatPayload(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object" && value !== null && (value as { __truncated?: boolean }).__truncated) {
    const t = value as { preview: string; bytes_total: number; bytes_shown: number };
    return `${t.preview}\n\n— truncated (${t.bytes_shown}/${t.bytes_total} bytes shown) —`;
  }
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Layout primitive used by every trace row.
 *
 * A 16px-wide left column hosts the marker (centered). The thread line is
 * drawn at the center of this column by the parent container, so the marker
 * naturally sits ON the line. The bg-background span around the icon punches
 * out the line behind it for a clean look.
 */
interface RowProps {
  marker: React.ReactNode;
  children: React.ReactNode;
}

function Row({ marker, children }: RowProps) {
  return (
    <div className="flex items-start gap-2">
      <div className="w-4 shrink-0 flex justify-center pt-[3px]">
        {/* Fixed-size opaque backdrop covers the thread line wherever the
            marker sits. z-10 makes the stacking explicit so the line never
            paints on top of the icon, regardless of the trace container's
            translucent backdrop. */}
        <span className="relative z-10 inline-flex items-center justify-center bg-background h-4 w-4">
          {marker}
        </span>
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

interface ToolStepProps {
  entry: ToolEntry;
  getToolLogo?: (toolName: string) => string | null;
}

function ToolStep({ entry, getToolLogo }: ToolStepProps) {
  const [open, setOpen] = useState(false);
  const hint = getArgHint(entry.args);
  const hasInspectable = !!entry.args || entry.result !== undefined;

  // Resolution order: explicit per-event logo → caller-provided lookup
  // (integration installed in the workspace) → Prodgy core fallback.
  const logoUrl = entry.integrationLogoUrl
    || getToolLogo?.(entry.toolName)
    || PRODGY_LOGO_URL;
  const logoAlt = entry.integration || "Prodgy";

  const marker = entry.status === "running" ? (
    <Loader2 className="h-3 w-3 animate-spin text-primary" />
  ) : (
    <Check className="h-3 w-3 text-green-500" />
  );

  return (
    <Row marker={marker}>
      <div
        className={cn(
          "flex items-center gap-2 text-xs",
          hasInspectable && "cursor-pointer hover:opacity-80 transition-opacity",
          entry.status === "running" ? "text-muted-foreground" : "text-muted-foreground/70",
        )}
        onClick={() => hasInspectable && setOpen(prev => !prev)}
        role={hasInspectable ? "button" : undefined}
        aria-expanded={hasInspectable ? open : undefined}
      >
        <span className="px-1.5 py-px rounded text-[9px] font-semibold uppercase tracking-wider bg-primary/10 text-primary/80 shrink-0">
          MCP
        </span>

        <img
          src={logoUrl}
          alt={logoAlt}
          className="h-3.5 w-3.5 rounded-sm object-contain shrink-0"
        />

        <span className={cn("font-mono", entry.status === "done" && "opacity-70")}>
          {entry.toolName}
          {hint && (
            <span className="text-muted-foreground/50 font-normal ml-1.5">({hint})</span>
          )}
        </span>

        {entry.status === "running" && entry.progress && (
          <span className="text-muted-foreground/60 truncate">{entry.progress}</span>
        )}

        {hasInspectable && (
          <ChevronDown
            className={cn(
              "h-3 w-3 ml-auto text-muted-foreground/40 transition-transform",
              open && "rotate-180",
            )}
          />
        )}
      </div>

      {open && hasInspectable && (
        <div className="mt-1 mb-1 rounded border border-border/40 bg-muted/30 overflow-hidden">
          {entry.args && Object.keys(entry.args).length > 0 && (
            <div>
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/60 bg-muted/40">
                args
              </div>
              <pre className="px-2 py-1.5 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words text-foreground/80 max-h-60 overflow-auto">
                {formatPayload(entry.args)}
              </pre>
            </div>
          )}
          {entry.result !== undefined && (
            <div className={cn(entry.args && Object.keys(entry.args).length > 0 && "border-t border-border/40")}>
              <div className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground/60 bg-muted/40">
                result
              </div>
              <pre className="px-2 py-1.5 text-[11px] leading-relaxed font-mono whitespace-pre-wrap break-words text-foreground/80 max-h-80 overflow-auto">
                {formatPayload(entry.result)}
              </pre>
            </div>
          )}
          {entry.status === "running" && entry.result === undefined && (
            <div className="px-2 py-1.5 text-[11px] text-muted-foreground/50 italic">
              aguardando retorno…
            </div>
          )}
        </div>
      )}
    </Row>
  );
}

interface TraceStepBlockProps {
  step: TraceStep;
  isStreaming: boolean;
  isLastStep: boolean;
  getToolLogo?: (toolName: string) => string | null;
}

function TraceStepBlock({ step, isStreaming, isLastStep, getToolLogo }: TraceStepBlockProps) {
  const showCursor = isStreaming && isLastStep && step.tools.length === 0;

  return (
    <div className="space-y-1">
      {step.text && (
        <Row marker={<span className="block w-1.5 h-1.5 rounded-full bg-muted-foreground/60" />}>
          <p className="text-xs text-muted-foreground/70 leading-relaxed py-0.5">
            {step.text.content}
            {showCursor && (
              <span className="inline-block w-[7px] h-[7px] rounded-full bg-primary ml-1 align-middle animate-cursor-glow" />
            )}
          </p>
        </Row>
      )}

      {step.tools.length > 0 && (
        // Sub-trace: when nested under a text step, indent by the parent's
        // row width (16px column + 8px gap = 24px ≈ ml-6) so the sub-line
        // sits centered under the parent text content (and clear of the
        // bullet). When the step has no parent text the tools render at top
        // level — same indentation as the rest of the trace.
        <div className={cn("relative", step.text && "ml-6")}>
          {/* Sub thread line — center of the 16px sub column. */}
          <div
            className="absolute top-1 bottom-1 w-px bg-border/40 z-0"
            style={{ left: '8px' }}
            aria-hidden
          />
          <div className="space-y-0.5">
            {step.tools.map((tool, idx) => (
              <ToolStep key={tool.toolCallId || idx} entry={tool} getToolLogo={getToolLogo} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ThinkingTrace({ entries, isStreaming = false, title = "Pensamento", getToolLogo, unavailableNote }: ThinkingTraceProps) {
  const [expanded, setExpanded] = useState<boolean>(isStreaming);
  const effectiveExpanded = isStreaming ? true : expanded;

  if (unavailableNote) {
    return (
      <div className="w-full py-1">
        <div className="px-4 max-w-3xl mx-auto flex items-center gap-1.5 text-xs py-0.5">
          <span className="font-medium text-muted-foreground/70">{title}</span>
          <span className="text-muted-foreground/30">·</span>
          <span className="text-muted-foreground/50">{unavailableNote}</span>
        </div>
      </div>
    );
  }

  if (entries.length === 0 && !isStreaming) return null;

  const steps = groupTraceEntries(entries);
  const hasTextSteps = steps.some(s => !!s.text);

  // Build a short summary chip rendered next to the header. Streaming shows
  // an active "pensando…" hint; the static state shows the tool count so
  // the user gets a sense of scale without expanding.
  const toolCount = entries.filter(e => e.kind === 'tool').length;
  const summary = isStreaming
    ? (toolCount > 0 ? `${toolCount} ${toolCount === 1 ? 'ação' : 'ações'} · pensando…` : 'pensando…')
    : (toolCount > 0 ? `${toolCount} ${toolCount === 1 ? 'ação' : 'ações'}` : '');

  return (
    <div className="w-full py-1">
      <div className="px-4 max-w-3xl mx-auto">
        {/* Notion-style inline toggle — no outer box, no border, just a
            chevron + title + summary. The expanded content (already
            self-contained with its own thread line + per-tool markers)
            flows directly below, anchored by its left structure. */}
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="group flex items-center gap-1.5 text-xs transition-colors py-0.5"
        >
          <ChevronDown
            className={cn(
              "h-3 w-3 shrink-0 transition-transform duration-200 text-muted-foreground/50 group-hover:text-foreground/70",
              !effectiveExpanded && "-rotate-90",
              isStreaming && "text-primary/60",
            )}
          />
          <span
            className={cn(
              "font-medium text-muted-foreground/70 group-hover:text-foreground/80",
              isStreaming && "text-primary/80 group-hover:text-primary",
            )}
          >
            {title}
          </span>
          {summary && (
            <>
              <span className="text-muted-foreground/30">·</span>
              <span
                className={cn(
                  "text-muted-foreground/50 group-hover:text-muted-foreground/70 truncate text-left max-w-[260px]",
                  isStreaming && "animate-pulse",
                )}
              >
                {summary}
              </span>
            </>
          )}
        </button>

        {effectiveExpanded && (
          <div className="relative pt-1.5 pb-1 space-y-2">
            {/* Main thread line — centered on the 16px marker column. With
                no outer box the column sits flush at the container edge,
                so the line center is at 8px from the container left.
                z-0 + `relative z-10` on the marker backdrop keeps the line
                visually beneath the icons. */}
            {hasTextSteps && (
              <div
                className="absolute top-3 bottom-3 w-px bg-border/60 z-0"
                style={{ left: '8px' }}
                aria-hidden
              />
            )}

            {steps.length === 0 && isStreaming && (
              <div className="flex items-center gap-1 py-1 pl-6">
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full animate-[typing_1.4s_ease-in-out_infinite]" />
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full animate-[typing_1.4s_ease-in-out_0.2s_infinite]" />
                <span className="w-1.5 h-1.5 bg-muted-foreground/30 rounded-full animate-[typing_1.4s_ease-in-out_0.4s_infinite]" />
              </div>
            )}

            {steps.map((step, idx) => (
              <TraceStepBlock
                key={idx}
                step={step}
                isStreaming={isStreaming}
                isLastStep={idx === steps.length - 1}
                getToolLogo={getToolLogo}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
